<script setup lang="ts">
import { appUrl } from '../config/runtime-config';
import { FACTS, INTRO, TAGLINE } from '../content/site-content';

import AuctionMock from './AuctionMock.vue';

/**
 * La prima schermata: marchio grande, che cos'è in una riga, come si entra.
 *
 * Il logo completo è ripetuto qui in grande anche se sta già nella barra: la
 * copertina è il posto dove il marchio va guardato, non riconosciuto di
 * sfuggita. È `brand/logo.svg` (marchio + lettering) e accanto non c'è un
 * «Falasta» scritto in HTML, che lo raddoppierebbe.
 */
</script>

<template>
  <section id="top" class="hero">
    <div class="page hero__grid">
      <div class="hero__text">
        <img class="hero__logo" src="/brand/logo.svg" width="380" height="108" alt="Falasta" />
        <h1 class="hero__title">{{ TAGLINE }}</h1>
        <p class="hero__lead">{{ INTRO }}</p>

        <div class="hero__actions">
          <a class="button button--accent button--lg" :href="appUrl">Entra nell’asta</a>
          <a class="button button--ghost button--lg" href="#come-funziona">Come funziona</a>
        </div>

        <p class="hero__note">
          L’amministratore si registra con email e password. I fantallenatori no: ricevono un link e
          basta.
        </p>
      </div>

      <div class="hero__mock">
        <AuctionMock />
      </div>
    </div>

    <div class="page">
      <dl class="facts">
        <div v-for="fact in FACTS" :key="fact.id" class="facts__item">
          <dt class="facts__value">{{ fact.value }}</dt>
          <dd class="facts__label">{{ fact.label }}</dd>
        </div>
      </dl>
    </div>
  </section>
</template>

<style scoped>
.hero {
  padding-block: clamp(2.5rem, 7vw, 4.5rem) clamp(2.5rem, 6vw, 4rem);
  /* Un velo di lime in alto a destra e di navy in basso a sinistra: i due colori
     del marchio, alla luminosità del tema, senza un'immagine da scaricare. */
  background:
    radial-gradient(60rem 30rem at 85% -10%, var(--color-accent-soft), transparent 70%),
    radial-gradient(50rem 28rem at 0% 110%, var(--color-primary-soft), transparent 70%);
}

.hero__grid {
  display: grid;
  gap: clamp(2rem, 5vw, 3.5rem);
  align-items: center;
}

.hero__logo {
  display: block;
  width: min(19rem, 78%);
  height: auto;
  margin-bottom: clamp(1.25rem, 3vw, 1.75rem);
}

.hero__title {
  font-size: clamp(2rem, 6vw, 3.1rem);
}

.hero__lead {
  max-width: 34rem;
  margin-top: 1.1rem;
  color: var(--color-muted);
  font-size: clamp(1rem, 2.4vw, 1.15rem);
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.75rem;
}

.hero__note {
  max-width: 30rem;
  margin-top: 1rem;
  color: var(--color-faint);
  font-size: 0.875rem;
}

.hero__mock {
  max-width: 26rem;
  width: 100%;
  justify-self: center;
}

.facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin: clamp(2.5rem, 6vw, 4rem) 0 0;
  padding-top: clamp(1.5rem, 4vw, 2rem);
  border-top: 1px solid var(--color-border);
}

.facts__item {
  min-width: 0;
}

.facts__value {
  color: var(--color-heading);
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.facts__label {
  margin: 0.25rem 0 0;
  color: var(--color-muted);
  font-size: 0.875rem;
}

@media (min-width: 60rem) {
  .hero__grid {
    grid-template-columns: 1.1fr 0.9fr;
  }

  .facts {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
